import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, AlertCircle } from "lucide-react";

export default function ConnectStatusCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/connect/status");
      setStatus(data);
    } catch { setStatus({ connected: false, charges_enabled: false }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [kycBlocked, setKycBlocked] = useState(null);

  const onboard = async () => {
    setBusy(true);
    setKycBlocked(null);
    try {
      // 30s timeout — a bit longer than the backend's 25s Stripe cutoff so we
      // get the API's clean error message instead of an axios timeout.
      const { data } = await api.post(
        "/connect/onboard",
        { origin_url: window.location.origin },
        { timeout: 30000 },
      );
      window.location.href = data.url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend returns a structured payload when KYC blocks payouts —
      // surface it inline (targeted CTA to /organizer/verify) instead of a toast.
      if (detail && typeof detail === "object" && detail.code === "kyc_required") {
        setKycBlocked(detail);
      } else if (e.code === "ECONNABORTED") {
        toast.error("Stripe is slow to respond right now. Give it 20 seconds and tap Connect again.");
      } else if (formatApiErrorDetail(detail)) {
        toast.error(formatApiErrorDetail(detail));
      } else if (e.response?.status >= 500) {
        toast.error("Couldn't reach Stripe. Please try again in a moment.");
      } else {
        toast.error("Could not start Stripe onboarding");
      }
    } finally { setBusy(false); }
  };

  if (!status) return null;
  const enabled = !!status.charges_enabled;
  const connected = !!status.connected;

  return (
    <div
      data-testid="connect-status-card"
      className="card-warm rounded-sm p-6 mb-8"
      style={{ borderLeft: `3px solid ${enabled ? "var(--moss)" : "var(--terracotta)"}` }}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="overline whitespace-nowrap" style={{ color: enabled ? "var(--moss)" : "var(--terracotta)" }}>
              Booth-fee payouts
            </div>
            {enabled ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(111,125,85,0.15)", color: "var(--moss)" }}>
                <CheckCircle2 className="h-3 w-3" /> Ready
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(31,93,52,0.15)", color: "var(--terracotta)" }}>
                <AlertCircle className="h-3 w-3" /> Action needed
              </span>
            )}
          </div>
          <div className="mt-2 font-serif text-2xl">
            {enabled
              ? "You can accept booth fees."
              : connected
              ? "Finish your Stripe onboarding to accept booth fees."
              : "Connect Stripe to accept booth fees."}
          </div>
          <p className="mt-2 text-sm max-w-xl" style={{ color: "var(--ink-soft)" }}>
            Vendors pay booth fees securely through AddVendTour. Funds are transferred directly to your Stripe
            account minus a 5% platform fee. Takes ~5 minutes — you&rsquo;ll need your legal name, address, and a bank
            account for payouts.
          </p>
        </div>
        <Button
          data-testid="connect-onboard-btn"
          onClick={onboard}
          disabled={busy}
          className={`rounded-full inline-flex items-center gap-2 self-start flex-shrink-0 ${enabled ? "" : "btn-primary"}`}
          variant={enabled ? "outline" : "default"}
        >
          <CreditCard className="h-4 w-4" />
          {enabled ? "Update payout details" : connected ? "Finish onboarding" : "Connect Stripe"}
        </Button>
      </div>

      {/* KYC-required guardrail: shown after the backend returns 403 with
          code=kyc_required. Deep-links to /organizer/verify + shows which
          of the 4 verification steps still need doing. */}
      {kycBlocked && (
        <div
          data-testid="connect-kyc-blocked"
          className="mt-5 rounded-lg p-4 border-2"
          style={{ backgroundColor: "rgba(249,115,22,0.06)", borderColor: "rgba(249,115,22,0.35)" }}
        >
          <div className="flex items-start gap-3">
            <span className="rounded-full p-2 flex-shrink-0"
                  style={{ backgroundColor: "rgba(249,115,22,0.15)", color: "var(--avt-orange)" }}>
              <CreditCard className="h-4 w-4" />
            </span>
            <div className="flex-1">
              <div className="font-serif text-lg">Verify your identity first</div>
              <p className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>
                {kycBlocked.message}
              </p>
              {kycBlocked.steps_done && (
                <ul className="mt-3 space-y-1 text-xs" data-testid="connect-kyc-steps">
                  {[
                    ["name", "Legal name"],
                    ["email", "Email"],
                    ["phone", "Phone"],
                    ["identity", "Government ID + selfie"],
                  ].map(([k, label]) => (
                    <li key={k} className="flex items-center gap-2">
                      <span data-testid={`connect-kyc-step-${k}`}
                            className={`inline-block w-3 h-3 rounded-full ${kycBlocked.steps_done[k] ? "" : "border"}`}
                            style={{
                              backgroundColor: kycBlocked.steps_done[k] ? "#84CC16" : "transparent",
                              borderColor: "rgba(0,0,0,0.2)",
                            }} />
                      <span style={{
                        color: kycBlocked.steps_done[k] ? "#4B7A0A" : "var(--ink-soft)",
                        textDecoration: kycBlocked.steps_done[k] ? "line-through" : "none",
                      }}>{label}</span>
                    </li>
                  ))}
                </ul>
              )}
              <a
                href={kycBlocked.verification_url || "/organizer/verify"}
                data-testid="connect-kyc-cta"
                className="btn-primary mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm"
              >
                <CreditCard className="h-4 w-4" /> Complete verification
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
